import { cn } from '@invent/wl-ui-kit-next';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleFormField,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import { ArrowLeft, Eye, Plus, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, useWorkspaceWidth } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import { definitionsApi } from '../api/definitions-api';
import type {
  IDefinitionRule,
  IDefinitionSection,
  IDefinitionUsage,
  IPublishFinding,
  IRuleEffectWrite,
  IUsageWrite,
  IVersionDocument,
  IVocabulary
} from '../api/definitions-contracts';
import { AdminPage, LoadError, TableSkeleton, useAdminData } from '../components/admin-page';
import {
  ComputeEditor,
  ParamsEditor,
  PrefillEditor,
  UnresolvedTypeNotice,
  type IPayloadContext
} from '../components/payload-editors';
import { EffectsEditor, PredicateEditor } from '../components/predicate-editor';
import { TEAM_PATHS, versionPreviewPath } from '../paths';
import { refusalMessage } from '../refusal';

/**
 * The definition editor — one survey version, and every write survey-api offers below it.
 *
 * ## The revision protocol, in one place
 *
 * Every mutation under `/versions/{id}` carries `If-Match` with the draft's `revision`. This
 * screen never guesses the next value: it re-reads the whole document after each write, and
 * `mutate` below is the only path a write takes, so no call site can forget either half.
 * That re-read is not defensive — a write can change more than it was given (a reorder
 * renumbers every sibling; a section delete cascades to its usages) so the document a screen
 * holds after one write is stale in ways it cannot compute.
 *
 * A 409 here means one of two very different things and the copy tells them apart: a **stale
 * revision** (somebody else edited; re-reading fixes it) or a **frozen definition** (this
 * version is published; re-reading never will).
 *
 * ## Bindings are read-only
 *
 * `bindings` come back on the document and no route writes one — workflow bindings are seeded
 * with the survey. They are shown because a reader looking at a definition wants to know a
 * registration gate exists, and hidden behind nothing because there is nothing to hide.
 */
export function AdminVersionEditorPage() {
  const { versionId = '' } = useParams();
  const navigate = useNavigate();
  // A workspace an author stays in: the rail folds to its icons for as long as it is open.
  useWorkspaceWidth('full');
  const [busy, setBusy] = React.useState(false);
  const [findings, setFindings] = React.useState<Nullable<IPublishFinding[]>>(null);

  const { data: document, error, setError, reload } = useAdminData<IVersionDocument>(
    (signal) => definitionsApi.version(versionId, signal),
    [versionId]
  );
  const { data: vocabulary } = useAdminData<IVocabulary>(() => definitionsApi.vocabulary());

  /**
   * The one path every write takes: run it with the current revision, then re-read.
   *
   * Callers hand in a function of the revision rather than the revision itself, so there is
   * no window in which a stale number could be captured into a closure and sent later.
   */
  const mutate = React.useCallback(
    async (run: (revision: number) => Promise<unknown>, fallback: string) => {
      if (!document) return false;
      setBusy(true);
      setError(null);
      try {
        await run(document.revision);
        await reload();
        return true;
      } catch (writeError) {
        setError(writeMessage(writeError, fallback));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [document, reload, setError]
  );

  const frozen = document !== null && document.status !== 'draft';

  return (
    <AdminPage
      title={document ? `${document.surveyCode} v${document.versionNo}` : 'Version'}
      crumb="Survey builder"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to={TEAM_PATHS.versions}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-[var(--color-royal-blue)]"
        >
          <ArrowLeft className="size-4" aria-hidden /> All versions
        </Link>

        {/* Offered on a published version as readily as on a draft: ADM-07 is about walking
            the cycle that is about to open, and that version is published by then. */}
        {document !== null && (
          <Button asChild variant="outline" size="sm">
            <Link to={versionPreviewPath(document.surveyVersionId)}>
              <Eye className="size-4" aria-hidden /> Preview
            </Link>
          </Button>
        )}
      </div>

      {error && <LoadError message={error} />}

      {document === null ? (
        <TableSkeleton rows={5} />
      ) : (
        <>
          <Lifecycle
            document={document}
            busy={busy}
            frozen={frozen}
            onFindings={setFindings}
            onGone={() => navigate(TEAM_PATHS.versions)}
            mutate={mutate}
            onFailed={setError}
          />

          {findings && <Findings findings={findings} onClose={() => setFindings(null)} />}

          <SectionsPanel document={document} frozen={frozen} busy={busy} mutate={mutate} />
          <UsagesPanel
            document={document}
            vocabulary={vocabulary}
            frozen={frozen}
            busy={busy}
            mutate={mutate}
          />
          <RulesPanel
            document={document}
            vocabulary={vocabulary}
            frozen={frozen}
            busy={busy}
            mutate={mutate}
          />
          <BindingsPanel document={document} />
        </>
      )}
    </AdminPage>
  );
}

type Mutate = (run: (revision: number) => Promise<unknown>, fallback: string) => Promise<boolean>;

/** "1 question", "0 questions" — every noun this screen counts is regular. */
function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/**
 * The refusals this surface can answer, as sentences.
 *
 * `definition-frozen` and a stale `revision-conflict` are both 409 and mean opposite things:
 * one is "somebody else got there first, re-read", the other is "this can never be written
 * again". Telling an author to retry a frozen write would be actively wrong.
 */
function writeMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    const type = error.problem?.type;
    if (type === ProblemType.definitionFrozen) {
      return 'This version is published or retired, so it is frozen — nothing below it can be changed. Clone it into a new draft instead.';
    }
    if (type === ProblemType.revisionConflict || error.status === 409) {
      return 'Somebody else changed this draft while you were editing. It has been re-read — check what you meant to change and try again.';
    }
    if (type === ProblemType.preconditionRequired) {
      return 'The request went out without a revision. That is a bug in this app rather than something you did.';
    }
  }
  return refusalMessage(error, fallback);
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

function Lifecycle({
  document,
  busy,
  frozen,
  onFindings,
  onGone,
  mutate,
  onFailed
}: {
  document: IVersionDocument;
  busy: boolean;
  frozen: boolean;
  onFindings: (findings: IPublishFinding[]) => void;
  onGone: () => void;
  mutate: Mutate;
  onFailed: (message: string) => void;
}) {
  const [notes, setNotes] = React.useState(document.notes ?? '');
  React.useEffect(() => setNotes(document.notes ?? ''), [document.notes]);
  // Publish and Retire are `definitions.publish`, apart from the authoring the screen opens on
  // (SR-228): drawn off with the reason for an author who cannot release.
  const canPublish = useCan()('definitions.publish');
  const publishReason = React.useId();

  const run = async (
    call: (revision: number) => Promise<{ findings: IPublishFinding[] }>,
    fallback: string
  ) => {
    // Publish and the dry run both answer findings, and a *failed* publish carries the same
    // list inside its 422 body. So both paths end at the same renderer.
    await mutate(async (revision) => {
      try {
        const result = await call(revision);
        onFindings(result.findings);
      } catch (error) {
        if (error instanceof ApiError && error.problem?.type === ProblemType.publishFailed) {
          onFindings(error.problem.findings ?? []);
          // Swallowed on purpose: the findings panel is the message, and a red banner
          // repeating "publish failed" above it would say less than the list does.
          return;
        }
        throw error;
      }
    }, fallback);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          {document.surveyCode} v{document.versionNo}
          <Badge variant={document.status === 'published' ? 'success' : 'soft'}>
            {document.status}
          </Badge>
          <Badge variant="outline">{document.origin}</Badge>
          <span className="text-xs font-normal text-muted-foreground">
            revision {document.revision}
          </span>
        </CardTitle>
        <CardDescription>
          {count(document.sections.length, 'section')} ·{' '}
          {count(document.usages.length, 'question')} ·{' '}
          {count(document.rules.length, 'rule')} ·{' '}
          {count(document.bindings.length, 'binding')}
          {document.publishedAt && ` · published ${formatDateTime(document.publishedAt)}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {frozen && (
          <Alert>
            <AlertTitle>Frozen</AlertTitle>
            <AlertDescription>
              A published or retired version rejects every write in the domain aggregate. To
              change this content, create a new draft cloned from it on the versions list.
            </AlertDescription>
          </Alert>
        )}

        {document.origin === 'seed' && (
          <Alert>
            <AlertTitle>A code seeder owns this version</AlertTitle>
            <AlertDescription>
              Its content comes from <code>*DefinitionSeeder</code>, which re-publishes it on
              every deploy. Editing it here would be overwritten; the seeder is the source.
            </AlertDescription>
          </Alert>
        )}

        <SimpleFormField label="Notes" htmlFor="version-notes">
          <Textarea
            id="version-notes"
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={frozen}
          />
        </SimpleFormField>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            disabled={busy || frozen || notes === (document.notes ?? '')}
            onClick={() =>
              void mutate(
                (revision) =>
                  definitionsApi.setVersionNotes(
                    document.surveyVersionId,
                    revision,
                    notes.trim() === '' ? null : notes
                  ),
                'Could not save the notes'
              )
            }
          >
            Save notes
          </Button>

          <Button
            variant="outline"
            disabled={busy}
            onClick={() =>
              void run(
                (revision) => definitionsApi.validate(document.surveyVersionId, revision),
                'Could not run the dry run'
              )
            }
          >
            Dry run
          </Button>

          {document.status === 'draft' && (
            <Button
              disabled={busy || !canPublish}
              aria-describedby={canPublish ? undefined : publishReason}
              onClick={() =>
                void run(
                  (revision) => definitionsApi.publish(document.surveyVersionId, revision),
                  'Could not publish'
                )
              }
            >
              Publish
            </Button>
          )}

          {document.status === 'published' && (
            <Button
              variant="outline"
              disabled={busy || !canPublish}
              aria-describedby={canPublish ? undefined : publishReason}
              onClick={() =>
                void run(
                  (revision) => definitionsApi.retire(document.surveyVersionId, revision),
                  'Could not retire'
                )
              }
            >
              Retire
            </Button>
          )}

          {document.status === 'draft' && (
            <Button
              variant="outline"
              className="ml-auto text-destructive"
              disabled={busy}
              onClick={async () => {
                setNotes('');
                try {
                  await definitionsApi.deleteVersion(
                    document.surveyVersionId,
                    document.revision
                  );
                  onGone();
                } catch (deleteError) {
                  onFailed(writeMessage(deleteError, 'Could not discard this draft'));
                }
              }}
            >
              <Trash2 />
              Discard draft
            </Button>
          )}
        </div>
        {!canPublish && document.status !== 'retired' && (
          <NotPermitted id={publishReason} what="publishing or retiring a version" />
        )}
      </CardContent>
    </Card>
  );
}

/** The dry run's answer, and a failed publish's — the same list either way. */
function Findings({
  findings,
  onClose
}: {
  findings: IPublishFinding[];
  onClose: () => void;
}) {
  const errors = findings.filter((finding) => finding.severity === 'error');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {findings.length === 0
            ? 'Nothing to report'
            : `${errors.length} error(s), ${findings.length - errors.length} warning(s)`}
        </CardTitle>
        <CardDescription>
          {findings.length === 0
            ? 'The validator found no reason to refuse this version.'
            : 'Errors stop a publish; warnings do not. The dry run does not materialize, so ' +
              '`not_materialized` appears for every column still inherited from the bank and ' +
              '`pin_unresolvable` can never appear at all.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-2">
        {findings.map((finding, index) => (
          <div
            key={`${finding.code}-${finding.subject}-${index}`}
            className="grid gap-0.5 rounded-md border border-border px-3 py-2 text-sm"
          >
            <span className="flex items-center gap-2">
              <Badge variant={finding.severity === 'error' ? 'destructive' : 'soft'}>
                {finding.severity}
              </Badge>
              <span className="font-mono text-xs">{finding.code}</span>
              {finding.subject && (
                <span className="font-mono text-xs text-muted-foreground">
                  {finding.subject}
                </span>
              )}
            </span>
            <span>{finding.message}</span>
          </div>
        ))}
        <Button variant="outline" className="w-fit" onClick={onClose}>
          Dismiss
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

function SectionsPanel({
  document,
  frozen,
  busy,
  mutate
}: {
  document: IVersionDocument;
  frozen: boolean;
  busy: boolean;
  mutate: Mutate;
}) {
  const [adding, setAdding] = React.useState(false);
  const [code, setCode] = React.useState('');
  const [title, setTitle] = React.useState('');

  const ordered = [...document.sections].sort((a, b) => a.ordinal - b.ordinal);

  const move = (section: IDefinitionSection, by: number) => {
    const at = ordered.findIndex((entry) => entry.code === section.code);
    const to = at + by;
    if (to < 0 || to >= ordered.length) return;
    const next = [...ordered];
    [next[at], next[to]] = [next[to], next[at]];
    void mutate(
      (revision) =>
        definitionsApi.reorderSections(
          document.surveyVersionId,
          revision,
          next.map((entry) => entry.code)
        ),
      'Could not reorder the sections'
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Sections</CardTitle>
        <CardDescription>
          The steps a respondent walks. Order is a whole-list write, so a move sends every
          code.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        {ordered.map((section, index) => (
          <div
            key={section.sectionId}
            className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2"
          >
            <span className="w-6 text-xs text-muted-foreground">{section.ordinal}</span>
            <span className="font-mono text-xs">{section.code}</span>
            <span className="text-sm">{section.title}</span>
            <span className="text-xs text-muted-foreground">
              {count(
                document.usages.filter((usage) => usage.section === section.code).length,
                'question'
              )}
            </span>
            {!frozen && (
              <span className="ml-auto flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy || index === 0}
                  aria-label={`Move ${section.code} up`}
                  onClick={() => move(section, -1)}
                >
                  ↑
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy || index === ordered.length - 1}
                  aria-label={`Move ${section.code} down`}
                  onClick={() => move(section, 1)}
                >
                  ↓
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  aria-label={`Delete ${section.code}`}
                  onClick={() =>
                    void mutate(
                      (revision) =>
                        definitionsApi.deleteSection(
                          document.surveyVersionId,
                          revision,
                          section.code
                        ),
                      'Could not delete this section'
                    )
                  }
                >
                  <Trash2 />
                </Button>
              </span>
            )}
          </div>
        ))}

        {ordered.length === 0 && (
          <p className="text-sm text-muted-foreground">No sections yet.</p>
        )}

        {!frozen &&
          (adding ? (
            <div className="flex flex-wrap items-end gap-2">
              <Input
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="S1"
                aria-label="Section code"
                className="max-w-[8rem] font-mono"
              />
              <Input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Section title"
                aria-label="Section title"
                className="max-w-sm"
              />
              <Button
                disabled={busy || code.trim() === '' || title.trim() === ''}
                onClick={async () => {
                  const ok = await mutate(
                    (revision) =>
                      definitionsApi.createSection(document.surveyVersionId, revision, {
                        code: code.trim(),
                        title,
                        // Appended: a missing ordinal is refused by name rather than
                        // silently moved to the front.
                        ordinal: ordered.length + 1,
                        intro: null
                      }),
                    'Could not add this section'
                  );
                  if (ok) {
                    setCode('');
                    setTitle('');
                    setAdding(false);
                  }
                }}
              >
                Add
              </Button>
              <Button variant="outline" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button variant="outline" className="w-fit" onClick={() => setAdding(true)}>
              <Plus />
              Add section
            </Button>
          ))}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Usages
// ---------------------------------------------------------------------------

function UsagesPanel({
  document,
  vocabulary,
  frozen,
  busy,
  mutate
}: {
  document: IVersionDocument;
  vocabulary: Nullable<IVocabulary>;
  frozen: boolean;
  busy: boolean;
  mutate: Mutate;
}) {
  const [editing, setEditing] = React.useState<Nullable<IDefinitionUsage>>(null);
  const [adding, setAdding] = React.useState(false);
  const [section, setSection] = React.useState<string>(ALL);

  const shown = React.useMemo(
    () =>
      [...document.usages]
        .filter((usage) => section === ALL || usage.section === section)
        .sort((a, b) => a.ordinal - b.ordinal),
    [document.usages, section]
  );

  /** Reorder is **per section** — the version has no single flat order to replace — so a
   *  move is only offered while one section is in view. */
  const move = (usage: IDefinitionUsage, by: number) => {
    const siblings = shown.filter((entry) => entry.section === usage.section);
    const at = siblings.findIndex((entry) => entry.code === usage.code);
    const to = at + by;
    if (to < 0 || to >= siblings.length) return;
    const next = [...siblings];
    [next[at], next[to]] = [next[to], next[at]];
    void mutate(
      (revision) =>
        definitionsApi.reorderUsages(
          document.surveyVersionId,
          revision,
          usage.section,
          next.map((entry) => entry.code)
        ),
      'Could not reorder the questions'
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Questions</CardTitle>
        <CardDescription>
          Each row places a bank question into this version. A column shown as{' '}
          <em>inherited</em> has no value of its own and reads the bank’s — editing it here is
          what breaks that link.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={section} onValueChange={setSection}>
            <SelectTrigger className="w-56" aria-label="Filter by section">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Every section</SelectItem>
              {[...document.sections]
                .sort((a, b) => a.ordinal - b.ordinal)
                .map((entry) => (
                  <SelectItem key={entry.sectionId} value={entry.code}>
                    {entry.code} · {entry.title}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">{count(shown.length, 'question')}</span>
          {!frozen && (
            <Button
              variant="outline"
              className="ml-auto"
              onClick={() => {
                setAdding(true);
                setEditing(null);
              }}
            >
              <Plus />
              Place a question
            </Button>
          )}
        </div>

        <div className="grid gap-1">
          {shown.map((usage, index) => {
            const open = editing?.usageId === usage.usageId;

            return (
              // The row and its open panel are one box in two pieces, so they are wrapped
              // together: the list's `gap-1` then separates questions from each other rather
              // than a question from its own form, and the two halves can meet on a shared
              // edge instead of across a gap.
              <div key={usage.usageId} className="grid">
                <div
                  className={cn(
                    'flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm',
                    open
                      ? 'rounded-b-none border-[var(--color-royal-blue)] bg-[var(--color-slight-blue)]/50 shadow-[inset_3px_0_0_var(--color-royal-blue)]'
                      : 'border-border'
                  )}
                >
                  <span className="w-6 text-xs text-muted-foreground">{usage.ordinal}</span>
                  <span className="font-mono text-xs">{usage.code}</span>
                  <span className="max-w-md truncate">
                    {usage.label ?? (
                      <span className="text-muted-foreground">
                        (inherited from {usage.question})
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {usage.fieldType ?? 'inherited'}
                  </span>
                  {usage.childRole && <Badge variant="outline">{usage.childRole}</Badge>}
                  {usage.requiredMode !== 'optional' && (
                    <Badge variant="soft">{usage.requiredMode}</Badge>
                  )}
                  {usage.visibilityDefault === 'hidden' && (
                    <Badge variant="outline">hidden</Badge>
                  )}
                  {/* Said in words as well as in colour: the accent alone is a convention a
                      first-time reader has to infer, and on a frozen version the panel below
                      is a reader rather than an editor. */}
                  {open && <Badge>{frozen ? 'viewing' : 'editing'}</Badge>}
                  <span className="ml-auto flex gap-1">
                    {!frozen && section !== ALL && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busy || index === 0}
                          aria-label={`Move ${usage.code} up`}
                          onClick={() => move(usage, -1)}
                        >
                          ↑
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busy || index === shown.length - 1}
                          aria-label={`Move ${usage.code} down`}
                          onClick={() => move(usage, 1)}
                        >
                          ↓
                        </Button>
                      </>
                    )}
                    {/* A toggle, so the control that opened the panel is the one that shuts
                        it — the Close button inside the panel is now a long way down it. */}
                    <Button
                      variant={open ? 'default' : 'outline'}
                      size="sm"
                      aria-expanded={open}
                      aria-label={`${open ? 'Close' : frozen ? 'View' : 'Edit'} ${usage.code}`}
                      onClick={() => {
                        setEditing(open ? null : usage);
                        setAdding(false);
                      }}
                    >
                      {open ? 'Close' : frozen ? 'View' : 'Edit'}
                    </Button>
                  </span>
                </div>

                {/* Under the row it belongs to, rather than under the whole list. Remounting
                    per row is deliberate: moving from one question to another rebuilds the
                    form from the new usage instead of relying on the reset effect. */}
                {open && (
                  <UsageEditor
                    inline
                    document={document}
                    vocabulary={vocabulary}
                    usage={usage}
                    creating={false}
                    frozen={frozen}
                    busy={busy}
                    mutate={mutate}
                    onClose={() => setEditing(null)}
                  />
                )}
              </div>
            );
          })}
          {shown.length === 0 && (
            <p className="py-4 text-sm text-muted-foreground">
              No question is placed here yet.
            </p>
          )}
        </div>

        {/* A new placement has no row to sit under, so it stays at the foot of the list. */}
        {adding && (
          <UsageEditor
            document={document}
            vocabulary={vocabulary}
            usage={null}
            creating
            frozen={frozen}
            busy={busy}
            mutate={mutate}
            onClose={() => setAdding(false)}
          />
        )}

        {section === ALL && !frozen && shown.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Pick one section to reorder its questions — usage order is per section, so there
            is no version-wide order to move a row within.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const ALL = 'all';

/**
 * One usage, created or edited.
 *
 * The write is whole-block, so this sends every column — including the ones left blank, which
 * is how a value is *cleared*. That is also why the inheritance flags matter on screen: a
 * blank `label` here means "inherit from the bank", and typing one is a deliberate override.
 *
 * `params`, `prefill` and `compute` are JSON text boxes. Their schemas are code-owned per
 * field type (`vocabulary.fieldTypes[].paramsSchema`), they differ per type, and this app
 * validates none of them — so it parses what was typed, refuses to send malformed JSON, and
 * leaves the rest to the server.
 *
 * `inline` is where it is mounted, not what it contains. Editing an existing placement opens
 * it directly under that row, carrying the row's accent so the two read as one thing; a new
 * placement has no row to sit under and gets the plain box at the foot of the list.
 */
function UsageEditor({
  document,
  vocabulary,
  usage,
  creating,
  frozen,
  busy,
  mutate,
  onClose,
  inline = false
}: {
  document: IVersionDocument;
  vocabulary: Nullable<IVocabulary>;
  usage: Nullable<IDefinitionUsage>;
  creating: boolean;
  frozen: boolean;
  busy: boolean;
  mutate: Mutate;
  onClose: () => void;
  inline?: boolean;
}) {
  const open = usage !== null || creating;
  const [form, setForm] = React.useState<IUsageWrite>(blankUsage);

  React.useEffect(() => {
    setForm(usage ? toUsageWrite(usage) : blankUsage());
  }, [usage, creating]);

  /**
   * Which parameter form to show depends on the field type, and a usage that inherits its
   * type does not carry one — `fieldType` is null and the bank row holds the answer. So the
   * bank is read when, and only when, the select says "inherit".
   */
  const inheritedType = useBankFieldType(form.question, form.fieldType === null);
  const effectiveType = form.fieldType ?? inheritedType;
  const typeSchema =
    (vocabulary?.fieldTypes ?? []).find((type) => type.code === effectiveType)?.paramsSchema ?? null;

  const payloadContext: IPayloadContext = {
    vocabulary,
    // A compute ref and a count-driven repeat both name another question in this version,
    // and the list of them is exactly the document's own usage codes.
    usageCodes: [...document.usages]
      .sort((a, b) => a.ordinal - b.ordinal)
      .map((entry) => entry.code),
    disabled: frozen
  };

  if (!open) return null;

  const set = (change: Partial<IUsageWrite>) => setForm((current) => ({ ...current, ...change }));

  const save = async () => {
    // No parse step any more: the editors below hold values, not text, so what `form` carries
    // is already the body. The JSON escape hatch inside them refuses to emit anything that
    // does not parse, which is what makes that true.
    const body: IUsageWrite = form;
    const ok = await mutate(
      (revision) =>
        usage
          ? definitionsApi.updateUsage(document.surveyVersionId, revision, usage.code, body)
          : definitionsApi.createUsage(document.surveyVersionId, revision, body),
      'Could not save this question'
    );
    if (ok) onClose();
  };

  return (
    <div
      className={cn(
        'grid gap-4 rounded-md border p-3',
        inline
          ? // Square across the top and no border there: the row directly above owns that
            // edge, and drawing a second one over it reads as a seam. The left accent is
            // repeated so the marker runs the height of the pair rather than stopping at
            // the row.
            'rounded-t-none border-t-0 border-[var(--color-royal-blue)] bg-[var(--color-slight-blue)]/20 shadow-[inset_3px_0_0_var(--color-royal-blue)]'
          : 'border-border'
      )}
    >
      <div className="flex items-center gap-2">
        <h3 className="font-mono text-sm font-medium">
          {usage?.code ?? 'New placement'}
        </h3>
        {usage && (
          <span className="text-xs text-muted-foreground">
            inherits{' '}
            {[
              usage.inherited.label && 'label',
              usage.inherited.fieldType && 'field type',
              usage.inherited.optionListVersion && 'option list'
            ]
              .filter(Boolean)
              .join(', ') || 'nothing'}
          </span>
        )}
        {/* Inline, the row's own Edit/Close toggle sits directly above this line, so a second
            Close beside it is two controls for one action. The create form has no row above
            it and keeps its own. */}
        {!inline && (
          <Button variant="outline" size="sm" className="ml-auto" onClick={onClose}>
            Close
          </Button>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <SimpleFormField label="Bank question" htmlFor="usage-question" required>
          <Input
            id="usage-question"
            value={form.question}
            onChange={(event) => set({ question: event.target.value })}
            disabled={frozen}
            className="font-mono text-xs"
            placeholder="area.thing_asked"
          />
        </SimpleFormField>

        <SimpleFormField label="Usage code" htmlFor="usage-code" required>
          <Input
            id="usage-code"
            value={form.code}
            onChange={(event) => set({ code: event.target.value })}
            disabled={frozen || !!usage}
            className="font-mono text-xs"
            placeholder="XX-S1-001"
          />
        </SimpleFormField>

        <SimpleFormField label="Section" htmlFor="usage-section">
          <Select
            value={form.section ?? NONE}
            onValueChange={(next) => set({ section: next === NONE ? null : next })}
            disabled={frozen}
          >
            <SelectTrigger id="usage-section">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>None</SelectItem>
              {document.sections.map((entry) => (
                <SelectItem key={entry.sectionId} value={entry.code}>
                  {entry.code} · {entry.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SimpleFormField>

        <SimpleFormField label="Field type (blank inherits)" htmlFor="usage-type">
          <Select
            value={form.fieldType ?? NONE}
            onValueChange={(next) => set({ fieldType: next === NONE ? null : next })}
            disabled={frozen}
          >
            <SelectTrigger id="usage-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Inherit from the bank</SelectItem>
              {(vocabulary?.fieldTypes ?? []).map((type) => (
                <SelectItem key={type.code} value={type.code}>
                  {type.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SimpleFormField>

        <SimpleFormField label="Visibility default" htmlFor="usage-visibility" required>
          <Select
            value={form.visibilityDefault ?? 'visible'}
            onValueChange={(next) => set({ visibilityDefault: next })}
            disabled={frozen}
          >
            <SelectTrigger id="usage-visibility">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(vocabulary?.enums.visibilityDefault ?? []).map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SimpleFormField>

        <SimpleFormField label="Required mode" htmlFor="usage-required" required>
          <Select
            value={form.requiredMode ?? 'optional'}
            onValueChange={(next) => set({ requiredMode: next })}
            disabled={frozen}
          >
            <SelectTrigger id="usage-required">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(vocabulary?.enums.requiredMode ?? []).map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SimpleFormField>

        <SimpleFormField label="Parent usage" htmlFor="usage-parent">
          <Input
            id="usage-parent"
            value={form.parentUsage ?? ''}
            onChange={(event) => set({ parentUsage: event.target.value || null })}
            disabled={frozen}
            className="font-mono text-xs"
          />
        </SimpleFormField>

        <SimpleFormField label="Child role" htmlFor="usage-childrole">
          <Select
            value={form.childRole ?? NONE}
            onValueChange={(next) => set({ childRole: next === NONE ? null : next })}
            disabled={frozen}
          >
            <SelectTrigger id="usage-childrole">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>None — a root question</SelectItem>
              {(vocabulary?.enums.childRole ?? []).map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SimpleFormField>

        <SimpleFormField label="Trigger option code" htmlFor="usage-trigger">
          <Input
            id="usage-trigger"
            value={form.triggerOptionCode ?? ''}
            onChange={(event) => set({ triggerOptionCode: event.target.value || null })}
            disabled={frozen}
            className="font-mono text-xs"
            placeholder="required by per_option_sub"
          />
        </SimpleFormField>

        <SimpleFormField label="Option list (blank inherits)" htmlFor="usage-list">
          <Input
            id="usage-list"
            value={form.optionList ?? ''}
            onChange={(event) => set({ optionList: event.target.value || null })}
            disabled={frozen}
            className="font-mono text-xs"
          />
        </SimpleFormField>

        <SimpleFormField label="Path scope" htmlFor="usage-path">
          <Input
            id="usage-path"
            value={form.pathScope ?? ''}
            onChange={(event) => set({ pathScope: event.target.value || null })}
            disabled={frozen}
            className="font-mono text-xs"
          />
        </SimpleFormField>

        <SimpleFormField label="Spec ref" htmlFor="usage-specref">
          <Input
            id="usage-specref"
            value={form.specRef ?? ''}
            onChange={(event) => set({ specRef: event.target.value || null })}
            disabled={frozen}
            className="font-mono text-xs"
            placeholder="RIA-PC-007"
          />
        </SimpleFormField>
      </div>

      <SimpleFormField label="Label (blank inherits from the bank)" htmlFor="usage-label">
        <Input
          id="usage-label"
          value={form.label ?? ''}
          onChange={(event) => set({ label: event.target.value || null })}
          disabled={frozen}
        />
      </SimpleFormField>

      <SimpleFormField label="Helper text" htmlFor="usage-helper">
        <Textarea
          id="usage-helper"
          rows={2}
          value={form.helperText ?? ''}
          onChange={(event) => set({ helperText: event.target.value || null })}
          disabled={frozen}
        />
      </SimpleFormField>

      {/* The three jsonb payloads, as forms. Each keeps a JSON view for the members the
          server's own grammar leaves open, and reports anything it does not render rather
          than dropping it — see `payload-editors.tsx`. */}
      <div className="grid gap-4">
        <div className="rounded-md border border-border p-3">
          {form.fieldType === null && effectiveType === null ? (
            <UnresolvedTypeNotice question={form.question} />
          ) : null}
          <ParamsEditor
            schema={typeSchema}
            fieldTypeKnown={typeSchema !== null}
            value={form.params ?? null}
            onChange={(next) => set({ params: next })}
            context={payloadContext}
          />
        </div>

        <div className="rounded-md border border-border p-3">
          <PrefillEditor
            value={form.prefill ?? null}
            onChange={(next) => set({ prefill: next })}
            context={payloadContext}
          />
        </div>

        <div className="rounded-md border border-border p-3">
          <ComputeEditor
            value={form.compute ?? null}
            onChange={(next) => set({ compute: next })}
            context={payloadContext}
          />
        </div>
      </div>

      {!frozen && (
        <div className="flex gap-2">
          <Button
            onClick={() => void save()}
            disabled={busy || form.question.trim() === '' || form.code.trim() === ''}
          >
            {usage ? 'Save question' : 'Place question'}
          </Button>
          {usage && (
            <Button
              variant="outline"
              className="text-destructive"
              disabled={busy}
              onClick={async () => {
                const ok = await mutate(
                  (revision) =>
                    definitionsApi.deleteUsage(
                      document.surveyVersionId,
                      revision,
                      usage.code
                    ),
                  'Could not delete this question'
                );
                if (ok) onClose();
              }}
            >
              <Trash2 />
              Remove from this version
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

const NONE = 'none';

function blankUsage(): IUsageWrite {
  return {
    question: '',
    code: '',
    section: null,
    specRef: null,
    label: null,
    helperText: null,
    fieldType: null,
    subjectOverride: null,
    visibilityDefault: 'visible',
    requiredMode: 'optional',
    optionList: null,
    optionListVersion: null,
    pathScope: null,
    parentUsage: null,
    childRole: null,
    triggerOptionCode: null,
    params: null,
    prefill: null,
    compute: null
  };
}

/** A read document, as the write body it is allowed to be — minus the read-only members the
 *  binder would ignore anyway (`usageId`, `ordinal`, `inherited`, the resolved
 *  `optionListVersionId`). */
function toUsageWrite(usage: IDefinitionUsage): IUsageWrite {
  return {
    question: usage.question,
    code: usage.code,
    section: usage.section,
    specRef: usage.specRef,
    label: usage.label,
    helperText: usage.helperText,
    fieldType: usage.fieldType,
    subjectOverride: usage.subjectOverride,
    visibilityDefault: usage.visibilityDefault,
    requiredMode: usage.requiredMode,
    optionList: usage.optionList,
    optionListVersion: usage.optionListVersion,
    pathScope: usage.pathScope,
    parentUsage: usage.parentUsage,
    childRole: usage.childRole,
    triggerOptionCode: usage.triggerOptionCode,
    params: usage.params,
    prefill: usage.prefill,
    compute: usage.compute
  };
}

/**
 * The bank question's field type, read only while the usage inherits it.
 *
 * Debounced because `question` is a free-text box and every keystroke would otherwise be a
 * request; a miss is not an error — the code may simply not name a bank row yet — so a
 * failure resolves to "unknown" and the parameters panel says so rather than showing one.
 */
function useBankFieldType(questionCode: string, enabled: boolean): Nullable<string> {
  const [fieldType, setFieldType] = React.useState<Nullable<string>>(null);

  React.useEffect(() => {
    const code = questionCode.trim();
    if (!enabled || code === '') {
      setFieldType(null);
      return;
    }

    let live = true;
    const timer = setTimeout(() => {
      definitionsApi
        .question(code)
        .then((row) => {
          if (live) setFieldType(row.fieldType);
        })
        .catch(() => {
          if (live) setFieldType(null);
        });
    }, 300);

    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [questionCode, enabled]);

  return fieldType;
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

/**
 * Rules, and the thing about them that costs an author an afternoon if nobody says it:
 * **a rule's effects are a separate write.**
 *
 * `POST /rules` and `PUT /rules/{code}` take the rule row only, and an `effects` array sent
 * to either is silently ignored by the binder. The effects are their own whole-block replace,
 * `PUT /rules/{code}/effects`. A rule created and left alone is inert, and the dry run says
 * so — `rule_without_effects`, plus `rule_predicate_missing` if it also has no predicate,
 * because a predicate-less rule must carry exactly one `set_state` effect.
 */
function RulesPanel({
  document,
  vocabulary,
  frozen,
  busy,
  mutate
}: {
  document: IVersionDocument;
  vocabulary: Nullable<IVocabulary>;
  frozen: boolean;
  busy: boolean;
  mutate: Mutate;
}) {
  const [editing, setEditing] = React.useState<Nullable<IDefinitionRule>>(null);
  const [adding, setAdding] = React.useState(false);

  const ordered = [...document.rules].sort(
    (a, b) => a.priority - b.priority || a.code.localeCompare(b.code)
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Rules</CardTitle>
        <CardDescription>
          Evaluated in <code>priority</code> order. A rule’s effects are written separately
          from the rule itself — see the panel below one.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        {ordered.map((rule) => {
          const open = editing?.ruleId === rule.ruleId;

          return (
            // Wrapped with its panel for the reason the questions list is: `gap-3` then
            // separates rules from each other rather than a rule from its own form.
            <div key={rule.ruleId} className="grid">
              <div
                className={cn(
                  'grid gap-1 rounded-md border px-3 py-2',
                  open
                    ? 'rounded-b-none border-[var(--color-royal-blue)] bg-[var(--color-slight-blue)]/50 shadow-[inset_3px_0_0_var(--color-royal-blue)]'
                    : 'border-border'
                )}
              >
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-mono text-xs">{rule.code}</span>
                  <span className="text-xs text-muted-foreground">priority {rule.priority}</span>
                  {rule.predicate === null && <Badge variant="outline">no predicate</Badge>}
                  <Badge variant={rule.effects.length === 0 ? 'destructive' : 'soft'}>
                    {rule.effects.length} effect{rule.effects.length === 1 ? '' : 's'}
                  </Badge>
                  {open && <Badge>{frozen ? 'viewing' : 'editing'}</Badge>}
                  <span className="ml-auto flex gap-1">
                    <Button
                      variant={open ? 'default' : 'outline'}
                      size="sm"
                      aria-expanded={open}
                      aria-label={`${open ? 'Close' : frozen ? 'View' : 'Edit'} ${rule.code}`}
                      onClick={() => {
                        setEditing(open ? null : rule);
                        setAdding(false);
                      }}
                    >
                      {open ? 'Close' : frozen ? 'View' : 'Edit'}
                    </Button>
                    {!frozen && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        aria-label={`Delete ${rule.code}`}
                        onClick={() =>
                          void mutate(
                            (revision) =>
                              definitionsApi.deleteRule(
                                document.surveyVersionId,
                                revision,
                                rule.code
                              ),
                            'Could not delete this rule'
                          )
                        }
                      >
                        <Trash2 />
                      </Button>
                    )}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {rule.effects.map((effect) => (
                    <span
                      key={effect.effectId}
                      className="rounded bg-[var(--color-slight-blue)]/50 px-2 py-0.5 font-mono text-xs"
                    >
                      {effect.effectType} →{' '}
                      {effect.targetUsage ?? effect.targetSection ?? 'response'}
                    </span>
                  ))}
                </div>
              </div>

              {open && (
                <RuleEditor
                  inline
                  document={document}
                  vocabulary={vocabulary}
                  rule={rule}
                  frozen={frozen}
                  busy={busy}
                  mutate={mutate}
                  onClose={() => setEditing(null)}
                />
              )}
            </div>
          );
        })}

        {ordered.length === 0 && <p className="text-sm text-muted-foreground">No rules.</p>}

        {/* A new rule has no row to sit under, so it stays at the foot of the list. */}
        {adding && (
          <RuleEditor
            document={document}
            vocabulary={vocabulary}
            rule={null}
            frozen={frozen}
            busy={busy}
            mutate={mutate}
            onClose={() => setAdding(false)}
          />
        )}

        {!frozen && (
          <Button
            variant="outline"
            className="w-fit"
            onClick={() => {
              setAdding(true);
              setEditing(null);
            }}
          >
            <Plus />
            Add rule
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * One rule, created or edited.
 *
 * Its form state is seeded from the props on mount and never re-seeded, which is safe only
 * because the panel is mounted per row: switching from one rule to another unmounts this and
 * mounts a new one. Rendering a single shared instance and swapping `rule` under it would
 * leave the previous rule's code, priority and JSON on screen under the new one's heading.
 *
 * `inline` is where it is mounted, not what it contains — see {@link UsageEditor}.
 */
function RuleEditor({
  document,
  vocabulary,
  rule,
  frozen,
  busy,
  mutate,
  onClose,
  inline = false
}: {
  document: IVersionDocument;
  vocabulary: Nullable<IVocabulary>;
  rule: Nullable<IDefinitionRule>;
  frozen: boolean;
  busy: boolean;
  mutate: Mutate;
  onClose: () => void;
  inline?: boolean;
}) {
  const [code, setCode] = React.useState(rule?.code ?? '');
  const [priority, setPriority] = React.useState(String(rule?.priority ?? 100));
  const [notes, setNotes] = React.useState(rule?.notes ?? '');
  const [predicate, setPredicate] = React.useState<Nullable<unknown>>(rule?.predicate ?? null);
  const [effects, setEffects] = React.useState<unknown[]>(
    () => rule?.effects.map(toEffectWrite) ?? []
  );

  const payloadContext: IPayloadContext = {
    vocabulary,
    usageCodes: [...document.usages]
      .sort((a, b) => a.ordinal - b.ordinal)
      .map((entry) => entry.code),
    disabled: frozen
  };

  const saveRule = async () => {
    const body = {
      code: code.trim(),
      predicate,
      priority: Number(priority) || 100,
      notes: notes.trim() === '' ? null : notes
    };
    const ok = await mutate(
      (revision) =>
        rule
          ? definitionsApi.updateRule(document.surveyVersionId, revision, rule.code, body)
          : definitionsApi.createRule(document.surveyVersionId, revision, body),
      'Could not save this rule'
    );
    if (ok && !rule) onClose();
  };

  const saveEffects = async () => {
    if (!rule) return;
    await mutate(
      (revision) =>
        definitionsApi.setRuleEffects(
          document.surveyVersionId,
          revision,
          rule.code,
          effects as IRuleEffectWrite[]
        ),
      'Could not save these effects'
    );
  };

  return (
    <div
      className={cn(
        'grid gap-4 rounded-md border p-3',
        inline
          ? 'rounded-t-none border-t-0 border-[var(--color-royal-blue)] bg-[var(--color-slight-blue)]/20 shadow-[inset_3px_0_0_var(--color-royal-blue)]'
          : 'border-border'
      )}
    >
      <div className="flex items-center gap-2">
        <h3 className="font-mono text-sm font-medium">{rule?.code ?? 'New rule'}</h3>
        {!inline && (
          <Button variant="outline" size="sm" className="ml-auto" onClick={onClose}>
            Close
          </Button>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <SimpleFormField label="Code" htmlFor="rule-code" required>
          <Input
            id="rule-code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            disabled={frozen || !!rule}
            className="font-mono text-xs"
          />
        </SimpleFormField>
        <SimpleFormField label="Priority" htmlFor="rule-priority">
          <Input
            id="rule-priority"
            value={priority}
            onChange={(event) => setPriority(event.target.value)}
            disabled={frozen}
            inputMode="numeric"
          />
        </SimpleFormField>
        <SimpleFormField label="Notes" htmlFor="rule-notes">
          <Input
            id="rule-notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={frozen}
          />
        </SimpleFormField>
      </div>

      <div className="rounded-md border border-border p-3">
        <PredicateEditor
          value={predicate}
          onChange={setPredicate}
          context={payloadContext}
          description="When this is true, the rule fires. Leaving it empty means it always fires — which the server requires of a rule whose only effect is set_state."
        />
      </div>

      {!frozen && (
        <Button className="w-fit" onClick={() => void saveRule()} disabled={busy || !code.trim()}>
          {rule ? 'Save rule' : 'Create rule'}
        </Button>
      )}

      {/* Effects are a second write against a rule that already exists, so they appear only
          once it does — creating a rule and its effects in one action is not a request the
          API offers. */}
      {rule && (
        <>
          <div className="rounded-md border border-border p-3">
            <EffectsEditor value={effects} onChange={setEffects} context={payloadContext} />
          </div>
          {!frozen && (
            <Button
              variant="outline"
              className="w-fit"
              onClick={() => void saveEffects()}
              disabled={busy}
            >
              Save effects
            </Button>
          )}
        </>
      )}
    </div>
  );
}

/** The read's effect minus the two members the API owns — it re-mints every id on a replace
 *  and numbers the array itself. */
function toEffectWrite(effect: IDefinitionRule['effects'][number]): IRuleEffectWrite {
  return {
    effectType: effect.effectType,
    targetKind: effect.targetKind,
    targetUsage: effect.targetUsage,
    targetSection: effect.targetSection,
    targetOptionCode: effect.targetOptionCode,
    severity: effect.severity,
    params: effect.params
  };
}

// ---------------------------------------------------------------------------
// Bindings
// ---------------------------------------------------------------------------

function BindingsPanel({ document }: { document: IVersionDocument }) {
  if (document.bindings.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Workflow bindings</CardTitle>
        <CardDescription>
          Read-only — no route writes one. They are seeded with the survey, and they are the
          gates that decide whether a response is created at all.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-1">
        {[...document.bindings]
          .sort((a, b) => a.ordinal - b.ordinal)
          .map((binding) => (
            <div key={binding.bindingId} className="text-sm">
              <span className="font-mono text-xs text-muted-foreground">{binding.hook}</span>{' '}
              <span className="font-mono text-xs">{binding.workflowCode}</span>
            </div>
          ))}
      </CardContent>
    </Card>
  );
}
